import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { contactInfo, navigationLinks } from '../data/cinematicContent';
import companyLogo from '../assets/portfolio/company_logo_new.png';

export const CinematicHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return;
    }

    const closeOnScroll = () => {
      setMobileMenuOpen(false);
    };

    // Close the mobile menu as soon as the page starts scrolling.
    window.addEventListener('scroll', closeOnScroll, { passive: true });
    window.addEventListener('touchmove', closeOnScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', closeOnScroll);
      window.removeEventListener('touchmove', closeOnScroll);
    };
  }, [mobileMenuOpen]);

  const scrollToSection = (href) => {
    setMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        isScrolled
          ? 'bg-[#e2eaf2]/94 backdrop-blur-xl py-4 shadow-[0_12px_35px_rgba(84,110,138,0.2)] border-b border-slate-300/70'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection('#home');
          }}
          className="flex items-center"
        >
          <div className={`h-[60px] w-[190px] md:h-[68px] md:w-[240px] transition-all duration-500 ${isScrolled ? 'opacity-100' : 'opacity-95'}`}>
            <img
              src={companyLogo}
              alt="LRJA Synthetic Granite Trading"
              className="h-full w-full object-contain object-left"
              loading="eager"
            />
          </div>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-10">
          {navigationLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(link.href);
              }}
              className="relative text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors duration-300
                after:absolute after:bottom-[-6px] after:left-0 after:w-0 after:h-[2px] after:bg-slate-500 after:transition-all after:duration-300 hover:after:w-full"
            >
              {link.name}
            </a>
          ))}
        </nav>

        {/* CTA Button */}
        <div className="hidden md:flex items-center gap-6">
          <a
            href={`tel:${contactInfo.phones[0]}`}
            className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors duration-300"
          >
            <Phone className="w-4 h-4" />
            {contactInfo.phones[0]}
          </a>
          <Button
            onClick={() => scrollToSection('#contact')}
            className="bg-slate-600 hover:bg-slate-500 text-slate-100 font-semibold px-6 transition-all duration-300 hover:scale-105"
          >
            Get Quote
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-2 rounded-xl text-slate-800 hover:bg-slate-200/70 transition-colors duration-300"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 bg-[#eef3f7]/98 backdrop-blur-xl shadow-[0_18px_45px_rgba(84,110,138,0.2)] transition-all duration-500 overflow-hidden ${
          mobileMenuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="flex flex-col py-6">
          {navigationLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(link.href);
              }}
              className="px-6 py-4 text-slate-700 hover:bg-slate-200/60 hover:text-slate-900 transition-colors duration-300 text-lg"
            >
              {link.name}
            </a>
          ))}
          <div className="px-6 pt-4 border-t border-slate-300 mt-4">
            <Button
              onClick={() => scrollToSection('#contact')}
              className="w-full bg-slate-600 hover:bg-slate-500 text-slate-100 font-semibold"
            >
              Get Quote
            </Button>
          </div>
        </nav>
      </div>
    </header>
  );
};
